import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAppFilters } from "../state/filters";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Skeleton } from "../components/ui/skeleton";
import { Badge } from "../components/ui/badge";
import { formatInt, formatMoney, formatPct } from "../lib/format";
import { Trophy, Target } from "lucide-react";
import ExportButton from "../components/ExportButton";

export default function SalesPerformance() {
  const { range } = useAppFilters();
  const [data, setData] = useState(null);
  const [goals, setGoals] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let dead = false;
    (async () => {
      setLoading(true);
      try {
        const [sp, g] = await Promise.all([
          api.get("/dashboard/sales", { params: { start: range.start, end: range.end } }),
          api.get("/settings/goals"),
        ]);
        if (!dead) {
          setData(sp.data);
          const gmap = {};
          for (const it of g.data.items || []) gmap[it.rep] = it.monthly_revenue_target;
          setGoals(gmap);
        }
      } finally {
        if (!dead) setLoading(false);
      }
    })();
    return () => { dead = true; };
  }, [range]);

  return (
    <div className="space-y-6" data-testid="page-sales">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold">Sales Performance</h1>
          <p className="text-sm text-muted-foreground">Rep leaderboard, close rates, goal attainment.</p>
        </div>
        <ExportButton path="/export/sales.csv" params={{ start: range.start, end: range.end }} filename="sales_performance.csv" testid="sales-export-csv" />
      </div>

      <Card data-testid="card-sales-leaderboard">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-sm font-medium"><Trophy size={16} /> Leaderboard</CardTitle>
          <span className="text-xs text-muted-foreground">{data?.reps?.length || 0} reps</span>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-4 space-y-2">{Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">#</TableHead>
                  <TableHead>Rep</TableHead>
                  <TableHead className="text-right">Revenue</TableHead>
                  <TableHead className="text-right">Sales</TableHead>
                  <TableHead className="text-right">Avg ticket</TableHead>
                  <TableHead className="text-right">Close rate</TableHead>
                  <TableHead className="text-right">Show rate</TableHead>
                  <TableHead className="text-right">Open pipeline</TableHead>
                  <TableHead className="text-right w-[180px]">Goal attainment</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data?.reps || []).map((r) => {
                  const goal = goals[r.rep] || 0;
                  const pct = goal > 0 ? Math.min(100, Math.round((r.revenue / goal) * 100)) : 0;
                  return (
                    <TableRow key={r.rep} className="row-hover" data-testid={`sales-row-${r.rep.replaceAll(" ", "-").toLowerCase()}`}>
                      <TableCell className="tabular-nums text-muted-foreground">{r.rank}</TableCell>
                      <TableCell className="font-medium">{r.rep}</TableCell>
                      <TableCell className="text-right tabular-nums">{formatMoney(r.revenue)}</TableCell>
                      <TableCell className="text-right tabular-nums">{formatInt(r.sales)}</TableCell>
                      <TableCell className="text-right tabular-nums">{formatMoney(r.avg_ticket)}</TableCell>
                      <TableCell className="text-right tabular-nums">{formatPct(r.close_rate)}</TableCell>
                      <TableCell className="text-right tabular-nums">{formatPct(r.show_rate)}</TableCell>
                      <TableCell className="text-right tabular-nums">{formatMoney(r.open_pipeline)}</TableCell>
                      <TableCell className="text-right">
                        {goal > 0 ? (
                          <div className="flex items-center gap-2 justify-end">
                            <div className="w-24 h-1.5 rounded-full bg-[hsl(var(--surface-3))] overflow-hidden">
                              <div className="h-full bg-[hsl(var(--positive))]" style={{ width: `${pct}%` }} />
                            </div>
                            <span className="tabular-nums text-xs text-muted-foreground w-10">{pct}%</span>
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">No goal</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {!(data?.reps?.length) && (
                  <TableRow><TableCell colSpan={9} className="text-center py-8 text-muted-foreground">No sales in this period</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
